/**
 * The sound controls, built once and reused everywhere they are needed: the
 * Settings modal's Game tab, and the arena match's own in-fight panel (the owner
 * must be able to turn the battle down without leaving the battle).
 *
 * Every control writes through `setAudioSettings`, which persists to localStorage
 * and applies to the live bed on the same frame — there is no "apply" step and no
 * second copy of the state to keep in sync.
 */

import { el } from '../dom';
import { getAudioSettings, playSfx, setAudioSettings } from './audio';

/** A labelled 0–100 slider bound to one audio setting. */
function volumeRow(label: string, key: 'musicVolume' | 'sfxVolume', onInput?: () => void): HTMLElement {
  const readout = el('span', 'font-mono text-[11px] text-text-dim w-9 text-right shrink-0');
  const input = el('input', 'flex-1 accent-[#e8c46a] min-w-0', {
    type: 'range', min: '0', max: '100', step: '1',
    value: String(Math.round(getAudioSettings()[key] * 100)),
  }) as HTMLInputElement;
  const paint = () => { readout.textContent = `${input.value}%`; };
  paint();
  input.addEventListener('input', () => {
    setAudioSettings({ [key]: Number(input.value) / 100 });
    paint();
    onInput?.();
  });
  // Releasing the SFX slider plays a sample at the new level — you should hear
  // what you just chose, not discover it during the next fight.
  if (key === 'sfxVolume') input.addEventListener('change', () => playSfx('ui_click'));
  return el('div', 'flex items-center gap-3 py-2.5 border-b border-border/60', {},
    el('div', 'text-sm w-24 shrink-0', {}, label), input, readout);
}

/** The master mute switch. Silences everything but remembers both volumes. */
function muteRow(onToggle?: () => void): HTMLElement {
  const cls = (on: boolean) =>
    `relative w-11 h-6 rounded-full transition-colors shrink-0 flex items-center px-0.5 ${on ? 'bg-gold' : 'bg-surface-2 border border-border'}`;
  const knob = el('span', 'block w-5 h-5 rounded-full bg-white transition-transform');
  const state = () => !getAudioSettings().muted;          // the switch reads "Sound ON"
  const track = el('button', cls(state()), {
    onClick: () => {
      setAudioSettings({ muted: state() });
      track.className = cls(state());
      knob.style.transform = state() ? 'translateX(20px)' : 'translateX(0)';
      onToggle?.();
    },
  }, knob);
  knob.style.transform = state() ? 'translateX(20px)' : 'translateX(0)';
  return el('div', 'flex items-center gap-3 py-2.5 border-b border-border/60', {},
    el('div', 'flex-1 min-w-0', {},
      el('div', 'text-sm', {}, 'Sound'),
      el('div', 'text-[11px] text-text-dim', {}, 'Master switch for music and effects')),
    track);
}

/**
 * The three rows, in the order they make sense: the master switch first, then
 * what it governs. `onChange` fires after any of them is touched.
 */
export function soundControlRows(onChange?: () => void): HTMLElement[] {
  return [
    muteRow(onChange),
    volumeRow('Music', 'musicVolume', onChange),
    volumeRow('Effects', 'sfxVolume', onChange),
  ];
}
